import { useEffect } from 'react';

type EventCallback = (payload: any) => void;

class EventEmitter {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  subscribe(eventType: string, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    
    this.listeners.get(eventType)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(eventType);
        }
      }
    };
  }

  emit(eventType: string, payload?: any): void {
    const callbacks = this.listeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error);
        }
      });
    }
  }
}

// Singleton instance
const eventBus = new EventEmitter();

/**
 * Hook for subscribing to LLM events (e.g., 'llm:done', 'llm:error')
 * 
 * @param eventType - The event type to listen for
 * @param callback - Function to call when event is emitted
 * 
 * @example
 * ```tsx
 * useLlmEvents('llm:done', (event) => {
 *   if (event.agent === 'ARCHITECT') {
 *     queryClient.invalidateQueries({ queryKey: ['demands'] });
 *   }
 * });
 * ```
 */
export function useLlmEvents(eventType: string, callback: EventCallback): void {
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(eventType, callback);
    return unsubscribe;
  }, [eventType, callback]);
}

/**
 * Emit an LLM event to all subscribers
 * 
 * @param eventType - The event type to emit
 * @param payload - Data to pass to subscribers
 */
export function emitLlmEvent(eventType: string, payload?: any): void {
  eventBus.emit(eventType, payload);
}
